import {Button} from '@jobseeker/lotus/elements';
import {
  Box,
  Typography as Typo,
  Card as MuiCard,
  styled,
  Link,
  TypographyProps,
  Skeleton,
  FormHelperText as MuiFormHelperText,
  FormControl as MuiFormControl,
  FormControl,
  TextField,
  Tooltip,
} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  HiristGreenTick,
  HiristMail,
  HiristTelephonic,
  Information,
} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {getOldLink} from '@jobseeker/lotus/utils';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {showErrorToast} from '@jobseeker/common-core/utils';
import {useFormik} from 'formik';
import {validationSchemaOtp} from '../../../../helpers/formValidator';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

const StyledTextField = styled(TextField)(() => ({
  borderRadius: '32px',
  '& .MuiOutlinedInput-root': {
    border: '1px solid var(--dividers, rgba(126, 126, 126, 0.20))',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    height: '40px',
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '500',
  },
}));

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const VerifyContactSettingWidget = (props: {loading: boolean}) => {
  const theme = useWrappedTheme();
  const {profile} = useSelector((state: AppState) => state.user);
  const {loading} = props;
  const [showEmailBanner, setShowEmailBanner] = useState(false);
  const [isResendClicked, setIsResendClicked] = useState(false);
  const [showPhoneBanner, setShowPhoneBanner] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const [phoneErrorText, setPhoneErrorText] = useState<any>('');
  const user = useSelector((state: AppState) => state.user.profile);
  const {profileStatus} = useSelector(
    (state: AppState) => state.user.completeness
  );
  const isLarge = useIsLargeDevice();

  const handleSendOtp = async (): Promise<0 | 1> => {
    try {
      const response = await userService.sendOtp({
        phone: profile?.phone,
        countryCode: profile?.countryCode,
        isForVerification: true,
      });
      if (response?.error) {
        showErrorToast(response.error, response);
        return 0;
      }
      return 1;
    } catch (error) {
      showErrorToast('Failed to send OTP', error);
      return 0;
    }
  };

  useEffect(() => {
    if (!user?.phone) {
      setPhoneErrorText(
        <>
          You haven’t added a phone number to your profile yet.{' '}
          <Link
            href="https://www.hirist.tech/myprofile"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
          >
            Click here
          </Link>{' '}
          to update your phone number.
        </>
      );
    } else {
      setPhoneErrorText('');
    }
  }, [user?.phone]);

  const handleVerifyClick = async () => {
    if (user?.phone?.length != 10 || user?.countryCode !== 91) {
      setPhoneErrorText('Invalid Number');
      return;
    }

    const result = await handleSendOtp();

    if (result === 1) {
      setShowPhoneBanner(true);
      console.log('OTP sent successfully');
    }
  };

  const handleResendEmail = async () => {
    try {
      const resInfo = await userService.sendVerifyEmail();

      if (resInfo?.error?.code == 429) {
        setErrorText(resInfo?.error?.message);
        return;
      }
      if (resInfo?.error?.message) {
        showErrorToast('Failed to resend verification email', resInfo);
        return;
      }
      enqueueSnackbar('Verification email resent', {variant: 'success'});

      setShowEmailBanner(true);
    } catch (error) {
      showErrorToast('An error occurred while resending the email', error);
    }
  };
  const TIME_LIMIT_FOR_RESEND = 30;
  const [seconds, setSeconds] = useState(TIME_LIMIT_FOR_RESEND);
  const timerRef = useRef<any>();

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setSeconds(count => count - 1);
    }, 1000);
  };

  useEffect(() => {
    if (seconds <= 0) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (seconds == TIME_LIMIT_FOR_RESEND) {
      startTimer();
    }
    console.log(seconds);
  }, [seconds]);

  const handleResendOtp = async () => {
    const phone = profile.phone;
    const countryCode = profile.countryCode;

    const response = await userService.sendOtp({
      phone,
      countryCode,
      isForVerification: true,
    });
    if (response.error) {
      showErrorToast(response.error, response);
      return;
    }
    setSeconds(TIME_LIMIT_FOR_RESEND);

    enqueueSnackbar('Code resent successfully', {variant: 'success'});
  };

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      otp: '',
      phone: profile.phone,
      countryCode: profile.countryCode,
    },
    validationSchema: validationSchemaOtp,
    onSubmit: async (values: any) => {
      let resp;

      resp = await userService.verifyContactDetails({otp: values.otp}, 'phone');

      if (resp?.error) {
        formik.setFieldError('otp', resp.error.message);
        return;
      }

      const profilePayload = {
        phone: values.phone,
        countryCode: values.countryCode,
        verifiedNumber: 1,
      };

      dispatch({
        type: 'User/set_user_profile',
        payload: profilePayload,
      });
    },
    validateOnChange: false,
  });

  const hasError = formik.touched.otp && Boolean(formik.errors.otp);
  const isInputEmpty = formik.values.otp.trim() === '';
  return (
    <Box height="100%" px={{xxs: 1.25, md: 0}}>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          px: {xxs: 1.875, md: 4.625},
          pb: {xxs: 1.875, md: 4.625},
          marginLeft: {md: 0.625},
          height: '100%',
        }}
      >
        <Box>
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={{xxs: 600, md: 500}}
              m={0}
              mb={1}
              mt={2.75}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Verify Contact Details
              <Tooltip
                disableHoverListener={!isLarge}
                disableFocusListener={!isLarge}
                disableTouchListener={!isLarge}
                placement="bottom-start"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    minWidth: '346px',
                    padding: '12px',
                  },
                }}
                title={
                  <>
                    <Typography
                      sx={{
                        whiteSpace: 'normal',
                        backgroundColor: '#1c2d41',
                        color: '#fff',
                        padding: '8px',
                        borderRadius: '4px',
                      }}
                    >
                      Candidates who verify their contact details are more
                      likely to get contacted by the recruiters.
                    </Typography>
                  </>
                }
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#1c2d41',
                      color: '#fff',
                    },
                  },
                  arrow: {
                    sx: {
                      color: '#1c2d41',
                    },
                  },
                }}
              >
                <Image
                  src={Information}
                  alt={'info'}
                  width={18}
                  height={18}
                  style={{marginLeft: 4}}
                />
              </Tooltip>
            </Typography>
          )}
          {loading ? (
            <Box px={1} pb={4}>
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
              {[
                ...new Array(5).fill('').map(() => (
                  <>
                    <Skeleton
                      variant="rounded"
                      width={'100%'}
                      height={14}
                      sx={{
                        my: 0.5,
                      }}
                    />
                  </>
                )),
              ]}
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
              <Skeleton
                variant="rounded"
                width={'50%'}
                height={21}
                sx={{
                  mt: 3,
                  mb: 1,
                }}
              />
            </Box>
          ) : (
            <Box mt={isLarge ? 0 : 3}>
              <Box
                display={'flex'}
                gap={1.5}
                flexDirection={{xxs: 'column', md: 'row'}}
                color="gray.dark"
                onClick={() => {}}
                data-testid="notification_list_item"
                sx={{
                  transition: 'all ease 0.2s',
                  cursor: 'pointer',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    mt: {xxs: 1, md: 5.375},
                    width: {xxs: '100%', md: '50%'},
                  }}
                >
                  <Box>
                    <Box display={'flex'} mt={-1}>
                      <Box
                        width={'100%'}
                        display={'flex'}
                        flexDirection={'column'}
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: 2,
                          padding: isLarge ? '33px 29px' : '20px 17px',
                          position: 'relative',
                        }}
                      >
                        {' '}
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            color: 'secondary.dark',
                            position: 'absolute',
                            top: -12,
                            left: 16,
                            backgroundColor: 'white',
                            padding: '0 8px',
                            fontWeight: 'bold',
                          }}
                        >
                          Verify Your Email Address
                        </Typography>
                        {!!user.verifiedEmail ? (
                          <Box
                            display={'flex'}
                            gap={3.125}
                            flexDirection={'column'}
                          >
                            <Box display="flex" gap={1.5} alignItems={'center'}>
                              <Image
                                src={HiristMail}
                                width={20}
                                height={15}
                                alt="successIcon"
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {user?.email}
                              </Typography>
                              <Image
                                src={HiristGreenTick}
                                width={18}
                                height={18}
                                alt="successIcon"
                              />
                            </Box>
                            <Typography
                              color={'#333333'}
                              fontWeight={500}
                              fontStyle="italic"
                              fontSize={{xxs: 14, md: 14}}
                              lineHeight={'22px'}
                              sx={{
                                lineClamp: '3',
                                '-webkit-line-clamp': '2',
                                '-webkit-box-orient': 'vertical',
                                borderRadius: 0.75,
                                padding: '5px 12px',
                                border: '1px solid #fbf0dd',
                                backgroundColor: '#fffcf7',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                display: 'block',
                                alignItems: 'center',
                              }}
                            >
                              Your email id is verified.
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Box display="flex" mb={2} gap={1.5}>
                              <Image
                                src={HiristMail}
                                width={24}
                                height={24}
                                alt="successIcon"
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {user?.email}
                              </Typography>
                            </Box>
                            <Box>
                              {!!!user.verifiedEmail && showEmailBanner ? (
                                <>
                                  <Typography
                                    component="div"
                                    fontStyle="italic"
                                    fontSize={14}
                                    color={'text.secondary'}
                                    sx={{
                                      display: 'inline-block',
                                      borderRadius: 0.75,
                                      padding: '7px 10px',
                                      border: '1px solid #fbf0dd',
                                      backgroundColor: '#fffcf7',
                                      wordWrap: 'break-word',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    Please check your inbox, We have sent you a
                                    verification link on your email id:{' '}
                                    <Box
                                      component="span"
                                      fontWeight={700}
                                      fontStyle="normal"
                                      color={theme.palette.primary.main}
                                      sx={{textTransform: 'none'}}
                                    >
                                      {user?.email}
                                    </Box>
                                  </Typography>
                                  <Button
                                    onClick={handleResendEmail}
                                    variant="outlined"
                                    disabled={isResendClicked}
                                    size="small"
                                    sx={{
                                      ...(!isLarge && {
                                        width: '100%',
                                      }),
                                      px: isLarge ? '34px' : '40px',
                                      py: isLarge ? '17px' : '13px',
                                      lineHeight: '24px',
                                      margin: '32px auto 0px auto',
                                      fontSize: '16px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Resend Verification Email
                                  </Button>
                                </>
                              ) : (
                                <Box>
                                  {errorText.length > 0 && (
                                    <Typography
                                      display="flex"
                                      fontStyle="italic"
                                      fontSize={14}
                                      fontWeight={300}
                                      sx={{
                                        mt: 3,
                                        borderRadius: 0.75,
                                        padding: '7px 10px',
                                        border: '1px solid #fbf0dd',
                                        backgroundColor: '#fffcf7',
                                        wordWrap: 'break-word',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      <Typography
                                        color={'text.primary'}
                                        variant="body3"
                                        lineHeight={'16px'}
                                      >
                                        {errorText}
                                      </Typography>
                                    </Typography>
                                  )}
                                  {isResendClicked && (
                                    <Button
                                      onClick={handleResendEmail}
                                      variant="outlined"
                                      disabled={isResendClicked}
                                      size="small"
                                      sx={{
                                        ...(!isLarge && {
                                          width: '100%',
                                        }),
                                        px: isLarge ? '34px' : '40px',
                                        py: isLarge ? '17px' : '13px',
                                        lineHeight: '24px',
                                        margin: '32px auto 0px auto',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                      }}
                                    >
                                      Resend Verification Email
                                    </Button>
                                  )}
                                </Box>
                              )}
                              {!!!user.verifiedEmail && !isResendClicked && (
                                <>
                                  <Button
                                    onClick={(
                                      e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                      setIsResendClicked(true);
                                      handleResendEmail();
                                    }}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      ...(!isLarge && {
                                        width: '100%',
                                      }),
                                      px: isLarge ? '34px' : '40px',
                                      py: isLarge ? '17px' : '13px',
                                      lineHeight: '24px',
                                      margin: '15px auto 0px auto',
                                      fontSize: '16px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Click here to Verify Email
                                  </Button>
                                </>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: {xxs: 2.5, md: 4.5},
                    width: {xxs: '100%', md: '50%'},
                  }}
                >
                  <Box>
                    <Box display={'flex'}>
                      <Box
                        display={'flex'}
                        flexDirection={'column'}
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: 2,
                          padding: isLarge ? '33px 29px' : '20px 17px',
                          position: 'relative',
                          width: '100%',
                        }}
                      >
                        {' '}
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            color: 'secondary.dark',
                            position: 'absolute',
                            top: -12,
                            left: 16,
                            backgroundColor: 'white',
                            padding: '0 8px',
                            fontWeight: 'bold',
                          }}
                        >
                          Verify your Phone Number
                        </Typography>
                        {!!user.verifiedNumber ? (
                          <Box display={'flex'} alignItems={'center'} gap={1}>
                            <Typography
                              color={'#333333'}
                              fontWeight={500}
                              fontStyle={'italic'}
                              fontSize={{xxs: 14, md: 16}}
                              lineHeight={'18px'}
                              sx={{
                                lineClamp: '3',
                                '-webkit-line-clamp': '2',
                                '-webkit-box-orient': 'vertical',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                wordWrap: 'break-word',
                                padding: '5px 12px',
                                border: '1px solid #fbf0dd',
                                backgroundColor: '#fffcf7',
                                display: 'block',
                                alignItems: 'center',
                              }}
                            >
                              Your phone number is verified.
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            {user?.phone && !phoneErrorText.length && (
                              <Box>
                                <Box display="flex" mb={2} gap={1.5}>
                                  <Image
                                    src={HiristTelephonic}
                                    width={18}
                                    height={18}
                                    alt="successIcon"
                                  />
                                  <Typography
                                    variant="body2"
                                    fontSize={13}
                                    color="#000"
                                    sx={{opacity: 0.6}}
                                  >
                                    +{user?.countryCode}-{user?.phone}
                                  </Typography>
                                  <Link
                                    sx={{
                                      fontSize: {xxs: '12px', lg: '16px'},
                                      cursor: 'pointer',
                                      height: 'fit-content',
                                      color: theme.palette.primary.main,
                                      textDecoration: 'underline',
                                      textDecorationColor:
                                        theme.palette.primary.main,
                                      textUnderlineOffset: '2px',
                                    }}
                                    href={'/registration/addPersonalDetails'}
                                    data-testid="open_in_mobile_app"
                                  >
                                    change?
                                  </Link>
                                </Box>
                                {!!!user?.verifiedNumber && showPhoneBanner && (
                                  <>
                                    <Typography
                                      component="div"
                                      fontStyle="italic"
                                      fontSize={14}
                                      fontWeight={300}
                                      color={'text.secondary'}
                                      mt={1}
                                      sx={{
                                        borderRadius: 0.75,
                                        padding: '5px 12px',
                                        border: '1px solid #fbf0dd',
                                        backgroundColor: '#fffcf7',
                                        display: 'inline-block',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      We have sent you an OTP on your number{' '}
                                      <span
                                        style={{
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {user?.phone}
                                      </span>
                                      . Please check your phone.
                                    </Typography>
                                    <Box
                                      component="form"
                                      width={'100%'}
                                      noValidate
                                      onSubmit={event => {
                                        event.preventDefault();
                                        formik.handleSubmit();
                                      }}
                                    >
                                      <FormControl
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          width: '100%',
                                          mt: 3,
                                        }}
                                        error={
                                          formik.touched.otp &&
                                          Boolean(formik.errors.otp)
                                        }
                                      >
                                        <StyledTextField
                                          sx={{
                                            '& .MuiInputBase-root': {
                                              height: '56px',
                                            },
                                          }}
                                          onKeyDown={(
                                            event: React.KeyboardEvent<HTMLInputElement>
                                          ) => {
                                            if (
                                              !/[0-9]/.test(event.key) &&
                                              event.key !== 'Backspace' &&
                                              event.key !== 'Enter'
                                            ) {
                                              event.preventDefault();
                                            }

                                            if (
                                              formik.values.otp.length >= 6 &&
                                              event.key !== 'Backspace'
                                            ) {
                                              event.preventDefault();
                                            }

                                            if (event.key === 'Enter') {
                                              formik.handleSubmit();
                                            }
                                          }}
                                          variant="outlined"
                                          type="text"
                                          name="otp"
                                          value={formik.values.otp}
                                          onChange={e => {
                                            formik.setFieldTouched(
                                              'otp',
                                              false
                                            );
                                            formik.handleChange(e);
                                          }}
                                          error={
                                            formik.touched.otp &&
                                            Boolean(formik.errors.otp)
                                          }
                                          placeholder="Enter OTP"
                                        />
                                        {formik?.touched?.otp &&
                                          Boolean(formik?.errors?.otp) && (
                                            <FormHelperText>
                                              {formik?.errors?.otp || ''}
                                            </FormHelperText>
                                          )}
                                        <Button
                                          variant="contained"
                                          label={'block_company'}
                                          size="small"
                                          color="secondary"
                                          data-testid="block_company"
                                          sx={{
                                            color: '#fff',
                                            fontSize: '14px',
                                            backgroundColor:
                                              theme.palette.primary.main,
                                            px: {xxs: 3, md: 3, lg: 4},
                                            py: 2,
                                            height: 'fit-content',
                                            fontWeight: '500',
                                            lineHeight: '23px',
                                            '&.Mui-disabled': {
                                              backgroundColor: '#e79e00',
                                              color: '#fff',
                                              border: 'none',
                                            },
                                            '&:hover': {
                                              backgroundColor:
                                                theme.palette.primary.main,
                                            },
                                          }}
                                          disabled={hasError || isInputEmpty}
                                          onClick={formik.handleSubmit}
                                          type="submit"
                                        >
                                          Confirm
                                        </Button>
                                      </FormControl>
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        ml={2}
                                      >
                                        <Typography
                                          variant="subtitle3"
                                          color={
                                            seconds > 0
                                              ? 'text.primary'
                                              : 'primary'
                                          }
                                          sx={{
                                            padding: '0px',
                                            mt:
                                              formik?.touched?.otp &&
                                              Boolean(formik?.errors?.otp)
                                                ? 2
                                                : '2px',

                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            '&:hover': {
                                              cursor: 'pointer',
                                              backgroundColor: 'transparent',
                                            },
                                          }}
                                        >
                                          {seconds > 0
                                            ? `Resend Code in ${seconds}s`
                                            : ''}
                                        </Typography>
                                      </Box>
                                      <Button
                                        onClick={async () => {
                                          if (seconds === 0) {
                                            await handleResendOtp();
                                          }
                                        }}
                                        disabled={seconds > 0}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          px: '24px',
                                          py: '14px',
                                          margin: '30px auto 0px auto',
                                          fontSize: '16px',
                                          fontWeight: 500,
                                        }}
                                      >
                                        Resend OTP
                                      </Button>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            )}
                            {phoneErrorText && (
                              <Typography
                                color={'#333333'}
                                fontWeight={500}
                                fontStyle={'italic'}
                                fontSize={14}
                                lineHeight={'18px'}
                                sx={{
                                  lineClamp: '3',
                                  '-webkit-line-clamp': '2',
                                  '-webkit-box-orient': 'vertical',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  wordWrap: 'break-word',
                                  padding: '5px 12px',
                                  border: '1px solid #fbf0dd',
                                  backgroundColor: '#fffcf7',
                                  display: 'block',
                                  alignItems: 'center',
                                }}
                              >
                                {phoneErrorText}
                              </Typography>
                            )}
                            {user?.phone &&
                              !!!user.verifiedNumber &&
                              !phoneErrorText.length &&
                              !showPhoneBanner && (
                                <>
                                  <Button
                                    onClick={handleVerifyClick}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      ...(!isLarge && {
                                        width: '100%',
                                      }),
                                      px: {xxs: '20px', lg: '34px'},
                                      py: isLarge ? '17px' : '13px',
                                      lineHeight: '24px',
                                      fontSize: '16px',
                                      mt: user?.phone
                                        ? {xxs: '18px', lg: '15px'}
                                        : undefined,
                                      fontWeight: 500,
                                    }}
                                  >
                                    Click here to Verify Phone
                                  </Button>
                                </>
                              )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default VerifyContactSettingWidget;
